import React, { useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import InfoIcon from '@mui/icons-material/Info'
import { Box, Card, CardContent, Typography } from '@mui/material'

import { MotivationStatsQueryParam, useGetMultipleMotivationStatsQuery } from '../../../../api/core'
import GRCircularProgress from '../../../../components/GRCircularProgress/GRCircularProgress'
import { SortOrder } from '../../../../components/GRTable/GRTable'
import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import analyticsService from '../../../../services/AnalyticsService'
import languageService from '../../../../services/LanguageService'
import motivationService from '../../../../services/MotivationService'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { useCompareGamesAccessCheck, useCompareGamesMotivationsAccessCheck } from '../../../account/hooks/roleHooks'
import { Game } from '../../../game'
import { GameWithMarketIso } from '../../../game-search/components/GameSearchDialog/GameSearchDialog'
import { GameAndAnalysis } from '../../../game/types/GameAndAnalysis'
import { MotivationKey, MotivationType } from '../../../market-explorer/types/MotivationType'
import { useGamesArcheTypes } from '../../hooks/useGamesArcheTypes'
import useGetMultipleMotivationProfile from '../../hooks/useGetMultipleMotivationProfile'
import { isWesternMarket, useAllMarketIsosAsian } from '../../hooks/useMarketMotivationsValidation'
import { ArchetypeDescriptionsModal } from '../ArchetypeDescriptionsModal/ArchetypeDescriptionsModal'
import MotivationArcheTypeCard from '../MotivationArcheTypeCard/MotivationArcheTypeCard'
import MotivationDriver from '../MotivationDriver/MotivationDriver'

export type SegmentMotivationsType = {
  [key in MotivationType]?: string
}

/**
 * MotivationsTabView show motivation tab view
 */
interface MotivationsTabViewProps {
  gamesWithMarketIso: GameWithMarketIso
  gamesAndAnalysis: GameAndAnalysis[]
}

const MotivationsTabView: React.FC<MotivationsTabViewProps> = ({ gamesWithMarketIso, gamesAndAnalysis }) => {
  useEffect(() => {
    analyticsService.trackEvent('Visited Game Comparison: Motivations')
  }, [])

  const gameIds = useMemo(() => Object.keys(gamesWithMarketIso), [gamesWithMarketIso])
  const motivationKeys = Object.keys(motivationService.keyMotivations) as MotivationKey[]
  const [modalMotivationKey, setModalMotivationKey] = useState<MotivationKey>()

  // Derive the per-game list from the already-loaded `gamesAndAnalysis` prop instead of
  // calling `games/list`, which returns HTTP 400 when a private own game is selected and
  // would otherwise leave the tab spinning forever (GR-2963). `gamesAndAnalysis` can contain
  // multiple entries per gameId (one per market iso), so dedupe by `game.id` and resolve one
  // `Game` per unique gameId, preserving the `gameIds` order the downstream code relies on.
  const gameLists = useMemo<Game[] | undefined>(() => {
    const gameById = new Map<string, Game>()
    gamesAndAnalysis.forEach(({ game }) => {
      if (game && !gameById.has(game.id)) {
        gameById.set(game.id, game)
      }
    })

    const resolved = gameIds.map((gameId) => gameById.get(gameId)).filter((game): game is Game => !!game)

    // If a selected game has not resolved yet (transient access-data loading), treat the list
    // as not ready so we keep the loading gate instead of rendering a partial comparison.
    return resolved.length === gameIds.length ? resolved : undefined
  }, [gamesAndAnalysis, gameIds])

  const hasCompareGamesAccess = useCompareGamesAccessCheck()
  const hasCompareGamesMotivationsAccess = useCompareGamesMotivationsAccessCheck()

  const allGamesAsian = useAllMarketIsosAsian(Object.values(gamesWithMarketIso).flat())

  const motivationStatsQueries: MotivationStatsQueryParam[] = useMemo(() => {
    if (!gameLists) {
      return []
    }
    return gameLists.map((game: Game) => ({
      appId: game.appId,
      conventionalSubgenreId: game.conventionalSubgenreId,
      gameId: game.id, // Include gameId for private games (appId = 0)
    }))
  }, [gameLists])

  const { data: originMotivationStats, isLoading: motivationStatsLoading } = useGetMultipleMotivationStatsQuery(
    { queryParams: motivationStatsQueries },
    { skip: !motivationStatsQueries.length }
  )

  const { data: motivationProfiles } = useGetMultipleMotivationProfile({ games: gameLists || [], skip: !gameLists })

  const gamesArcheTypes = useGamesArcheTypes({ gameLists, gamesWithMarketIso, motivationKeys, motivationProfiles })

  const motivationStats = useMemo(() => {
    if (!originMotivationStats || !motivationProfiles) return

    return [...originMotivationStats].map((motivationStat, motivationIndex) => {
      // If motivationStat is null (game has no conventionalSubgenreId)
      const modifiedMotivationStat = motivationStat ? { ...motivationStat } : {}
      Object.values(MotivationType).forEach((motivationTypeKey) => {
        if (motivationProfiles[motivationIndex] && motivationProfiles[motivationIndex].motivationValues) {
          modifiedMotivationStat[motivationTypeKey] = {
            ...modifiedMotivationStat[motivationTypeKey],
            game: motivationProfiles[motivationIndex].motivationValues?.[motivationTypeKey],
          }
        }
      })
      return modifiedMotivationStat
    })
  }, [motivationProfiles, originMotivationStats])

  const labels = useMemo(() => {
    if (!motivationStats) {
      return []
    }

    return Object.values(MotivationType).map((motivationKey) => {
      return languageService.getTranslation('motivations', motivationKey)
    })
  }, [motivationStats])

  const datasets = useMemo(() => {
    if (!gameLists || !motivationStats) {
      return []
    }

    return motivationStats.map((motivationStat, index) => {
      const data = Object.values(MotivationType).map((motivationOrder) => Math.round((motivationStat[motivationOrder]?.game || 0) * 10) / 10)

      const gameId = gameLists?.[index]?.id ?? null
      const marketIsos = gamesWithMarketIso?.[gameId] || []
      const isWesternGame = marketIsos.some((iso) => isWesternMarket(iso))

      return {
        label: gameLists[index]?.resolvedName,
        data: isWesternGame ? data : [], // Return empty data for non-Western games
      }
    })
  }, [gameLists, gamesWithMarketIso, motivationStats])

  const isLoading = motivationStatsLoading
  const isOutOfSync = !gameLists || gameLists.length !== gameIds.length
  if (isLoading || isOutOfSync) {
    return <GRCircularProgress />
  }

  if (!hasCompareGamesAccess) {
    return (
      <Box sx={{ mb: 3 }}>
        <LockedFeature.Card lockedFeatureId={LockedFeatureId.CompareGames} />
      </Box>
    )
  } else if (!hasCompareGamesMotivationsAccess) {
    return (
      <Box sx={{ mb: 3 }}>
        <LockedFeature.Card lockedFeatureId={LockedFeatureId.CompareMotivationTab} />
      </Box>
    )
  }

  if (allGamesAsian) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="row" justifyContent="center" alignContent="center">
            <InfoIcon color="primary" />
            <Typography marginLeft="5px">
              <Trans i18nKey={'motivations:motivation_na_for_market'} />
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box>
      {!!motivationStats && (
        <MotivationDriver
          datasets={datasets}
          labels={labels}
          motivationStats={motivationStats}
          gameLists={gameLists}
          gamesWithMarketIso={gamesWithMarketIso}
          gamesAndAnalysis={gamesAndAnalysis}
          sortBy={gameLists[0].id}
          sortByOrder={SortOrder.DESC}
        />
      )}

      <MotivationArcheTypeCard
        onSelectModalMotivationKey={() => setModalMotivationKey(motivationKeys[0])}
        onSelectMotivation={setModalMotivationKey}
        archeTypeList={gamesArcheTypes}
        titleTranslationKey={'motivations:player_archetypes'}
      />

      {modalMotivationKey && (
        <ArchetypeDescriptionsModal datasets={datasets} labels={labels} onSetMotivationKey={setModalMotivationKey} motivationKey={modalMotivationKey} />
      )}
    </Box>
  )
}

export default MotivationsTabView
